import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn.js';

export const cardVariants = cva('rounded-card bg-surface-raised transition-shadow', {
  variants: {
    variant: {
      /** Hairline border, no shadow — the default for dense layouts. */
      default: 'border border-border-default',
      /**
       * Lifted by a soft shadow. Dark mode adds the hairline back: a black
       * shadow on a near-black page separates nothing.
       */
      elevated: 'shadow-soft dark:border dark:border-border-default',
      /** Filled rather than outlined, for grouping inside a card. */
      muted: 'bg-surface-sunken',
      dashed: 'border border-dashed border-border-strong bg-transparent',
      /** The one yellow panel per screen. */
      brand: 'bg-accent text-accent-text',
    },
    interactive: {
      true: 'cursor-pointer hover:shadow-lift',
      false: '',
    },
  },
  defaultVariants: { variant: 'default', interactive: false },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, variant, interactive, ...props },
  ref,
) {
  return (
    <div ref={ref} className={cn(cardVariants({ variant, interactive }), className)} {...props} />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between gap-4 px-6 pb-4 pt-5', className)}
        {...props}
      />
    );
  },
);

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, ...props }, ref) {
    return (
      <h3
        ref={ref}
        className={cn('font-display text-base font-bold leading-tight', className)}
        {...props}
      />
    );
  },
);

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...props }, ref) {
  return <p ref={ref} className={cn('mt-1 text-sm text-text-muted', className)} {...props} />;
});

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn('px-6 pb-5', className)} {...props} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-3 border-t border-border-default px-6 py-4', className)}
        {...props}
      />
    );
  },
);
